import {
  cloneItem,
  copyItemAudioToPage,
  copyItemImagesToPage,
  copyItemVideoToPage,
} from '../services/PageAssetMigration';
import { insertItemIntoSection } from '../utils/itemMove';
import { displayNameFromPath } from '../utils/markdownPaths';
import { insertPathInNavOrder } from '../utils/navOrder';
import type { CheckItem, DatabaseFile, Section } from '../types';
import type { AppContext } from './appContext';
import { logSaveDebug } from '../utils/saveDebugLog';
import { saveFile } from './fileLifecycle';

export type PendingNewPageFromMove = {
  sourcePath: string;
  newPath: string;
  pageName: string;
  starterContent: string;
  item: CheckItem;
};

export type PendingCrossPageAssetCopy = {
  sourcePath: string;
  targetPath: string;
  items: CheckItem[];
};

export type PendingItemMoveState = {
  touchedPaths: Set<string>;
  assetCopies: PendingCrossPageAssetCopy[];
  newPages: PendingNewPageFromMove[];
};

export function emptyPendingItemMoveState(): PendingItemMoveState {
  return { touchedPaths: new Set(), assetCopies: [], newPages: [] };
}

export function clearPendingItemMoves(ctx: AppContext): void {
  ctx.pendingItemMoves = emptyPendingItemMoveState();
}

export function trackMoveTouched(ctx: AppContext, path: string): void {
  ctx.pendingItemMoves.touchedPaths.add(path);
}

export function registerCrossPageAssetCopy(
  ctx: AppContext,
  sourcePath: string,
  targetPath: string,
  items: CheckItem[]
): void {
  if (sourcePath === targetPath || items.length === 0) return;
  const state = ctx.pendingItemMoves;
  const existing = state.assetCopies.find(
    c => c.sourcePath === sourcePath && c.targetPath === targetPath
  );
  if (existing) {
    existing.items.push(...items.map(cloneItem));
  } else {
    state.assetCopies.push({
      sourcePath,
      targetPath,
      items: items.map(cloneItem),
    });
  }
  trackMoveTouched(ctx, targetPath);
}

export function registerPendingNewPage(ctx: AppContext, entry: PendingNewPageFromMove): void {
  ctx.pendingItemMoves.newPages.push(entry);
  trackMoveTouched(ctx, entry.sourcePath);
}

function itemsSectionOnPage(file: DatabaseFile): Section {
  let section = file.sections.find(s => s.title === 'Items');
  if (!section) {
    section = { title: 'Items', level: 2, items: [], prose: [] };
    file.sections.push(section);
  }
  return section;
}

/** Paths that need saveFile after pending move commits (includes primary). */
export async function commitPendingItemMoves(ctx: AppContext, primaryPath: string): Promise<string[]> {
  const state = ctx.pendingItemMoves;
  const savePaths = new Set<string>([primaryPath, ...state.touchedPaths]);

  for (const copy of state.assetCopies) {
    for (const item of copy.items) {
      await copyItemImagesToPage(ctx.github, copy.sourcePath, copy.targetPath, item);
      await copyItemAudioToPage(ctx.github, copy.sourcePath, copy.targetPath, item);
      await copyItemVideoToPage(ctx.github, copy.sourcePath, copy.targetPath, item);
    }
    savePaths.add(copy.sourcePath);
    savePaths.add(copy.targetPath);
  }

  for (const np of state.newPages) {
    await copyItemImagesToPage(ctx.github, np.sourcePath, np.newPath, np.item);
    await copyItemAudioToPage(ctx.github, np.sourcePath, np.newPath, np.item);
    await copyItemVideoToPage(ctx.github, np.sourcePath, np.newPath, np.item);

    const newFile = ctx.markdown.parse(np.newPath, np.starterContent, '');
    insertItemIntoSection(itemsSectionOnPage(newFile), np.item);
    newFile.name = displayNameFromPath(np.newPath);
    ctx.fileRegistry.originalContents.set(np.newPath, np.starterContent);

    const existingIdx = ctx.fileRegistry.files.findIndex(f => f.path === np.newPath);
    if (existingIdx >= 0) {
      ctx.fileRegistry.files[existingIdx] = newFile;
    } else {
      ctx.fileRegistry.files.push(newFile);
    }
    savePaths.add(np.newPath);
    savePaths.add(np.sourcePath);
  }

  return [...savePaths];
}

export async function saveFileAndPendingMoves(ctx: AppContext, file: DatabaseFile): Promise<void> {
  logSaveDebug({
    phase: 'pending-moves-start',
    path: file.path,
    ok: true,
    message: 'Committing pending item moves',
    details: {
      newPages: ctx.pendingItemMoves.newPages.length,
      touchedPaths: [...ctx.pendingItemMoves.touchedPaths],
    },
  });
  const newPagePaths = ctx.pendingItemMoves.newPages.map(np => np.newPath);
  const paths = await commitPendingItemMoves(ctx, file.path);
  logSaveDebug({
    phase: 'pending-moves-paths',
    path: file.path,
    ok: true,
    message: `Saving ${paths.length} file(s)`,
    details: { paths },
  });
  for (const path of paths) {
    const target = ctx.fileRegistry.files.find(f => f.path === path);
    if (target) await saveFile(ctx, target);
  }
  for (const newPath of newPagePaths) {
    if (ctx.allPaths.includes(newPath)) continue;
    ctx.allPaths.push(newPath);
    ctx.allPaths.sort((a, b) => a.localeCompare(b));
    ctx.navOrderFile = insertPathInNavOrder(ctx.navOrderFile, newPath);
  }
  if (newPagePaths.length > 0) {
    try {
      await ctx.navOrder.persist();
    } catch {
      /* ignore */
    }
  }
  clearPendingItemMoves(ctx);
}

/** Reload other pages touched by staged moves when discarding the open file. */
export function revertPendingItemMoveSideFiles(ctx: AppContext, discardingPath: string): void {
  for (const path of ctx.pendingItemMoves.touchedPaths) {
    if (path === discardingPath) continue;
    const content = ctx.fileRegistry.originalContents.get(path);
    if (content === undefined) continue;
    const idx = ctx.fileRegistry.files.findIndex(f => f.path === path);
    const sha = idx >= 0 ? ctx.fileRegistry.files[idx]!.sha : '';
    const fresh = ctx.markdown.parse(path, content, sha);
    if (idx >= 0) ctx.fileRegistry.files[idx] = fresh;
  }
}
